import { createGlobalStyle } from 'styled-components';
import fonts from './fonts';
import variables from './variables';
import TransitionStyles from './TransitionStyles';
import PrismStyles from './PrismStyles';

const GlobalStyle = createGlobalStyle`
  ${fonts};
  ${variables};

  html {
    box-sizing: border-box;
    width: 100%;
    scroll-behavior: smooth;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  ::selection {
    background-color: rgba(86, 112, 246, 0.2);
    color: var(--text);
  }

  :focus {
    outline: 2px dashed var(--blue);
    outline-offset: 3px;
  }

  :focus:not(:focus-visible) {
    outline: none;
    outline-offset: 0;
  }

  :focus-visible {
    outline: 2px dashed var(--blue);
    outline-offset: 3px;
  }

  html {
    scrollbar-width: thin;
    scrollbar-color: #c3cce0 var(--paper-soft);
  }
  ::-webkit-scrollbar {
    width: 12px;
  }
  ::-webkit-scrollbar-track {
    background: var(--paper-soft);
  }
  ::-webkit-scrollbar-thumb {
    background-color: #c3cce0;
    border: 3px solid var(--paper-soft);
    border-radius: 10px;
  }

  body {
    margin: 0;
    width: 100%;
    min-height: 100%;
    overflow-x: hidden;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    background-color: var(--paper);
    color: var(--text-soft);
    font-family: var(--font-sans);
    font-size: var(--fz-md);
    line-height: 1.6;

    @media (max-width: 480px) {
      font-size: var(--fz-sm);
    }

    &.hidden {
      overflow: hidden;
    }

    &.blur {
      overflow: hidden;

      #content > * {
        filter: blur(6px) brightness(0.96);
        transition: var(--transition);
        pointer-events: none;
        user-select: none;
      }
    }
  }

  #root {
    min-height: 100vh;
    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-columns: 100%;
  }

  main {
    width: 100%;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 14px 0;
    color: var(--text);
    font-weight: 600;
    line-height: 1.12;
    letter-spacing: -0.015em;
  }

  h1 {
    font-size: clamp(2.5rem, 5.4vw, 4.25rem);
  }

  h2 {
    font-size: clamp(2rem, 3.6vw, 3rem);
  }

  h3 {
    font-size: clamp(1.3rem, 2vw, 1.65rem);
  }

  p {
    margin: 0 0 16px 0;

    &:last-child,
    &:last-of-type {
      margin: 0;
    }

    & > a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  ul {
    &.fancy-list {
      ${({ theme }) => theme.mixins.fancyList};
    }
  }

  img,
  svg,
  .gatsby-image-wrapper {
    max-width: 100%;
    vertical-align: middle;
  }

  svg {
    vertical-align: middle;
  }

  a {
    color: inherit;
    text-decoration: none;
    transition: var(--transition);

    &:hover,
    &:focus {
      color: var(--blue);
    }

    &.inline-link {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  button {
    cursor: pointer;
    font-family: inherit;
    border: 0;
    background: none;
    color: inherit;
  }

  input,
  textarea {
    font-family: inherit;
    border-radius: var(--radius-sm);
    outline: 0;
  }

  hr {
    background-color: var(--line);
    height: 1px;
    border: 0;
    margin: 2rem 0;
  }

  code {
    font-family: var(--font-mono);
    font-size: 0.9em;
  }

  .container {
    ${({ theme }) => theme.mixins.container};
  }

  .section {
    ${({ theme }) => theme.mixins.section};
  }

  .eyebrow {
    ${({ theme }) => theme.mixins.eyebrow};
  }

  .text-grad {
    background: var(--gradient);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  /* Тёмные секции (hero, CTA) */
  .section--dark {
    background-color: var(--ink-950);
    color: var(--on-dark-soft);

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: #ffffff;
    }

    .eyebrow {
      color: var(--blue-bright);
    }

    a:hover,
    a:focus {
      color: #ffffff;
    }
  }

  .skip-to-content {
    ${({ theme }) => theme.mixins.primaryButton};
    position: absolute;
    top: -200px;
    left: 16px;
    z-index: 200;

    &:focus,
    &:active {
      top: 16px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    html {
      scroll-behavior: auto;
    }

    *,
    *:before,
    *:after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
    }
  }

  ${TransitionStyles};

  ${PrismStyles};
`;

export default GlobalStyle;
